/**
 * Tag Service - CRUD de Tags (Contato & Ticket)
 *
 * Usa authFetch de @/utils/api para chamadas autenticadas.
 * Endpoints: /api/inbox/tags/contact e /api/inbox/tags/ticket
 */
import { authFetch } from "@/utils/api";

const API_BASE = "/api/inbox/tags";

// ═══════════════════════════════════════════════════════════════
// CONTACT TAGS
// ═══════════════════════════════════════════════════════════════

/**
 * Listar todas as tags de contato da conta
 * @returns {Promise<Array<{id, name, color, createdAt, updatedAt}>>}
 */
export async function fetchContactTags() {
  const response = await authFetch(`${API_BASE}/contact`);
  if (!response.ok) throw new Error("Falha ao buscar tags de contato");
  return response.json();
}

/**
 * Criar nova tag de contato
 * @param {{ name: string, color?: string }} data
 * @returns {Promise<{id, name, color}>}
 */
export async function createContactTag(data) {
  const response = await authFetch(`${API_BASE}/contact`, {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (response.status === 409) {
    const err = await response.json();
    throw new Error(err.error || "Tag já existe");
  }
  if (!response.ok) throw new Error("Falha ao criar tag de contato");
  return response.json();
}

/**
 * Atualizar tag de contato
 * @param {string} tagId
 * @param {{ name?: string, color?: string }} data
 * @returns {Promise<{id, name, color}>}
 */
export async function updateContactTag(tagId, data) {
  const response = await authFetch(`${API_BASE}/contact/${tagId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error("Falha ao atualizar tag de contato");
  return response.json();
}

/**
 * Excluir tag de contato (soft delete)
 * @param {string} tagId
 * @returns {Promise<void>}
 */
export async function deleteContactTag(tagId) {
  const response = await authFetch(`${API_BASE}/contact/${tagId}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error("Falha ao excluir tag de contato");
  return response.json();
}

// ═══════════════════════════════════════════════════════════════
// TICKET TAGS
// ═══════════════════════════════════════════════════════════════

/**
 * Listar todas as tags de ticket da conta
 * @returns {Promise<Array<{id, name, color, createdAt, updatedAt}>>}
 */
export async function fetchTicketTags() {
  const response = await authFetch(`${API_BASE}/ticket`);
  if (!response.ok) throw new Error("Falha ao buscar tags de ticket");
  return response.json();
}

/**
 * Criar nova tag de ticket
 * @param {{ name: string, color?: string }} data
 * @returns {Promise<{id, name, color}>}
 */
export async function createTicketTag(data) {
  const response = await authFetch(`${API_BASE}/ticket`, {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (response.status === 409) {
    const err = await response.json();
    throw new Error(err.error || "Tag já existe");
  }
  if (!response.ok) throw new Error("Falha ao criar tag de ticket");
  return response.json();
}

/**
 * Atualizar tag de ticket
 * @param {string} tagId
 * @param {{ name?: string, color?: string }} data
 * @returns {Promise<{id, name, color}>}
 */
export async function updateTicketTag(tagId, data) {
  const response = await authFetch(`${API_BASE}/ticket/${tagId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error("Falha ao atualizar tag de ticket");
  return response.json();
}

/**
 * Excluir tag de ticket (soft delete)
 * @param {string} tagId
 * @returns {Promise<void>}
 */
export async function deleteTicketTag(tagId) {
  const response = await authFetch(`${API_BASE}/ticket/${tagId}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error("Falha ao excluir tag de ticket");
  return response.json();
}

// ═══════════════════════════════════════════════════════════════
// TAG ASSIGNMENTS
// ═══════════════════════════════════════════════════════════════

/**
 * Atribuir tag a um contato
 * @param {string} contactId
 * @param {string} tagId
 */
export async function assignContactTag(contactId, tagId) {
  const response = await authFetch(`${API_BASE}/assign/contact/${contactId}`, {
    method: "POST",
    body: JSON.stringify({ tagId }),
  });
  if (!response.ok) throw new Error("Falha ao atribuir tag ao contato");
  return response.json();
}

/**
 * Remover tag de um contato
 * @param {string} contactId
 * @param {string} tagId
 */
export async function unassignContactTag(contactId, tagId) {
  const response = await authFetch(
    `${API_BASE}/assign/contact/${contactId}/${tagId}`,
    {
      method: "DELETE",
    },
  );
  if (!response.ok) throw new Error("Falha ao remover tag do contato");
  return response.json();
}

/**
 * Atribuir tags em massa a múltiplos contatos
 * @param {string[]} contactIds
 * @param {string[]} tagIds
 * @returns {Promise<{ count: number }>}
 */
export async function bulkAssignContactTags(contactIds, tagIds) {
  const response = await authFetch(`${API_BASE}/assign/contact/bulk`, {
    method: "POST",
    body: JSON.stringify({ contactIds, tagIds }),
  });
  if (!response.ok) throw new Error("Falha ao atribuir tags em massa");
  return response.json();
}

/**
 * Atribuir tag a um ticket
 * @param {string} ticketId
 * @param {string} tagId
 */
export async function assignTicketTag(ticketId, tagId) {
  const response = await authFetch(`${API_BASE}/assign/ticket/${ticketId}`, {
    method: "POST",
    body: JSON.stringify({ tagId }),
  });
  if (!response.ok) throw new Error("Falha ao atribuir tag ao ticket");
  return response.json();
}

/**
 * Remover tag de um ticket
 * @param {string} ticketId
 * @param {string} tagId
 */
export async function unassignTicketTag(ticketId, tagId) {
  const response = await authFetch(
    `${API_BASE}/assign/ticket/${ticketId}/${tagId}`,
    {
      method: "DELETE",
    },
  );
  if (!response.ok) throw new Error("Falha ao remover tag do ticket");
  return response.json();
}
